import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import { getLanguage } from "@jmc/solid-design-system/src/utils/languages";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { default as cmsMapper } from "@utils/cms-mapper";
import classnames from "classnames";
import React from "react";

import Block from "../Block";
import style from "../style.module.scss";
import { trackingHelpersProps } from "../types";

interface SectionBlockProps {
    content: CMSBlock["section"];
    placement?: string;
    locale?: string;
    trackingHelpers: trackingHelpersProps;
    index?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    noBottomSpacing?: boolean;
    noTopSpacing?: boolean;
}

export const SectionBlock = ({
    content,
    locale,
    placement,
    trackingHelpers,
    siteSpecialty,
    index,
    noBottomSpacing = false,
    noTopSpacing = false,
}: SectionBlockProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    trackingHelpers.numRows = 0;
    trackingHelpers.numPanes = 0;
    trackingHelpers.numTabs = 0;
    trackingHelpers.position = 0;
    const sectionProps = cmsMapper.mapSection(content);
    const firstComponent = content?.content?.[0]?.component?.component?.[0];
    const firstComponentType = firstComponent?.internal?.type?.replace("Contentstack_component_", "");
    const noPadding =
        ((!jnjFullBranded || !sectionProps.showTitle) && firstComponentType === "hero") ||
        (!jnjFullBranded && firstComponentType === "country_selector") ||
        sectionProps?.noVerticalPadding;
    const fullWidth = jnjFullBranded ? true : sectionProps?.fullWidth;
    const textColor = jnjFullBranded ? "text-02" : sectionProps?.textColor;
    const decorative_image = jnjFullBranded ? undefined : sectionProps?.decorative_image?.[0];
    const titleColor = jnjFullBranded ? "text-01" : cmsMapper.mapSectionTextColors(sectionProps?.titleColor);
    const refColor = jnjFullBranded ? "primary-550" : sectionProps?.refColor;
    const visitedLinkColor = sectionProps?.visitedLinkColor;
    const languageDirection = !locale ? 1 : getLanguage(locale?.split("-").shift())?.dir;

    const isDesktop = !useMediaQuery(BreakPoint.xl);

    const getSectionLangDir = (): string => {
        const dir = sectionProps?.reverseTextDirection ? !languageDirection : !!languageDirection;
        return dir ? "ltr" : "rtl";
    };

    const visual = decorative_image?.visual;
    const decorativeImageStyles =
        isDesktop && visual
            ? {
                  backgroundImage: `url(${visual?.url})`,
                  backgroundPosition: `${
                      decorative_image?.position_horizontal === "center"
                          ? decorative_image?.position_horizontal
                          : decorative_image?.position_horizontal + " " + (decorative_image?.offset_horizontal || 0)
                  }  ${
                      decorative_image?.position_vertical === "center"
                          ? decorative_image?.position_vertical
                          : decorative_image?.position_vertical + " " + (decorative_image?.offset_vertical || 0)
                  }`,
              }
            : {};

    const section = (
        <section
            className={classnames(
                style.section,
                noPadding ? style.section__nopadding : null,
                noBottomSpacing ? style.section__noBottomSpacing : null,
                noTopSpacing ? style.section__noTopSpacing : null,
                style[sectionProps?.backgroundColor],
                fullWidth ? null : style.section__borderRadius,
                fullWidth ? null : style.containerWidth,
                isDesktop && visual ? style.decorativeImage : null,
            )}
            id={cleanCSSIdentifier(sectionProps?.title)}
            data-test-id="Section"
            dir={getSectionLangDir()}
            style={{
                ...decorativeImageStyles,
            }}
        >
            <div
                className={classnames(
                    style.content,
                    style[sectionProps?.backgroundColor],
                    fullWidth ? null : style.content__containerWidth,
                )}
            >
                {sectionProps.showTitle && (
                    <span
                        style={
                            {
                                "--text-link": `var(${
                                    jnjFullBranded ? "primary-550" : cmsMapper.mapSectionTextColors(refColor)
                                })`,
                            } as React.CSSProperties
                        }
                        className={classnames(
                            titleColor === "inherit" ? style.primary_section : "",
                            jnjFullBranded ? style.section_title : style[`section_title${titleColor}`],
                        )}
                    >
                        {jnjFullBranded ? (
                            <Typography variant="h2" data-test-id="sectionTitle" data-rebranded={jnjFullBranded}>
                                <JWMHtml showGlossary={false}>{sectionProps?.title}</JWMHtml>
                            </Typography>
                        ) : (
                            <Typography
                                variant="h2"
                                color="inherit"
                                data-test-id="sectionTitle"
                                data-rebranded={jnjFullBranded}
                            >
                                <JWMHtml showGlossary={false}>{sectionProps?.title}</JWMHtml>
                            </Typography>
                        )}
                    </span>
                )}
                <div>
                    {content?.content?.map((c: CMSBlock, i: number) => {
                        return (
                            <Block
                                textColor={textColor}
                                refColor={refColor}
                                visitedLinkColor={visitedLinkColor}
                                key={i}
                                content={c}
                                placement={`${placement}|section:${sectionProps?.title?.toString().toLowerCase()}`}
                                locale={locale}
                                fullWidth={fullWidth}
                                sectionBackground={sectionProps?.backgroundColor}
                                sectionIndex={index}
                                siteSpecialty={siteSpecialty}
                                sectionContent={content}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
    return jnjFullBranded ? section : <div className={fullWidth ? null : style.section__containerWidth}>{section}</div>;
};
